<template>
  <v-row dense class="mb-4">
    <template v-if="loading">
      <v-col v-for="n in skeletonCount" :key="`skeleton-${n}`" cols="12" sm="6" :lg="columnSize">
        <v-card class="pa-4 h-100" rounded="lg" elevation="0" border min-height="120" aria-busy="true">
          <div class="d-flex align-center ga-3">
            <div class="skeleton skeleton-icon flex-shrink-0"></div>

            <div class="skeleton skeleton-title"></div>
          </div>

          <div class="d-flex align-center justify-space-between ga-3 mt-4">
            <div class="skeleton skeleton-value"></div>

            <div class="skeleton skeleton-trend"></div>
          </div>
        </v-card>
      </v-col>
    </template>

    <template v-else>
      <v-col v-for="(item, index) in displayItems" :key="`${item.title}-${index}`" cols="12" sm="6" :lg="columnSize">
        <v-card class="metric-card pa-4 h-100 position-relative overflow-hidden" rounded="lg" elevation="0" border
          min-height="120" :style="cardVariables(item, index)">
          <div class="card-decoration"></div>

          <div class="d-flex align-center justify-space-between ga-2 position-relative">
            <div class="d-flex align-center ga-3 flex-grow-1 overflow-hidden">
              <v-avatar class="metric-icon flex-shrink-0" rounded="lg" size="38">
                <v-icon :icon="item.icon || 'mdi-chart-box-outline'" size="21" color="white" />
              </v-avatar>

              <div class="text-body-2 font-weight-bold text-high-emphasis text-truncate">
                {{ item.title }}
              </div>
            </div>

            <v-tooltip v-if="item.trend && item.percentage != null" location="top">
              <template #activator="{ props }">
                <v-chip v-bind="props" :color="item.trend === 'down' ? 'error' : 'success'" variant="tonal"
                  size="x-small" class="font-weight-bold flex-shrink-0" :prepend-icon="item.trend === 'down'
                      ? 'mdi-arrow-down'
                      : 'mdi-arrow-up'
                    ">
                  {{ item.percentage }}%
                </v-chip>
              </template>

              <span>{{ item.trendLabel || "vs. yesterday" }}</span>
            </v-tooltip>
          </div>

          <div class="d-flex align-center mt-4 position-relative">
            <div class="text-h5 font-weight-black text-high-emphasis">
              {{ formatValue(item) }}
            </div>
          </div>

          <div v-if="item.subtitle" class="text-caption font-weight-medium text-medium-emphasis mt-2 position-relative">
            {{ item.subtitle }}
          </div>

          <v-tooltip v-if="
            item.previousMonthValue != null ||
            item.currentMonthValue != null
          " location="top">
            <template #activator="{ props }">
              <v-icon v-bind="props" icon="mdi-information-outline" size="16"
                class="position-absolute bottom-0 right-0 ma-3 text-medium-emphasis" />
            </template>

            <div class="text-body-2">
              <div>
                Previous Month:
                <strong>{{ item.previousMonthValue ?? "—" }}</strong>
              </div>

              <div>
                Current Month:
                <strong>{{ item.currentMonthValue ?? "—" }}</strong>
              </div>
            </div>
          </v-tooltip>
        </v-card>
      </v-col>

      <v-col v-if="safeItems.length > 4" cols="12" class="d-flex justify-center mt-2">
        <v-btn variant="outlined" color="primary" size="small" rounded="lg" class="text-none font-weight-bold"
          :append-icon="showAll ? 'mdi-chevron-up' : 'mdi-chevron-down'" @click="showAll = !showAll">
          {{ showAll ? "View Less" : "View All" }}
        </v-btn>
      </v-col>
    </template>
  </v-row>
</template>

<script>
import { mapState } from "pinia";
import { useUiStore } from "@/store";

export default {
  name: "InformationCard",

  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      showAll: false,

      palettes: [
        {
          start: "#FF8A25",
          end: "#FF6900",
          decoration: "rgba(255, 155, 42, 0.18)",
        },
        {
          start: "#4AA6FF",
          end: "#2476F2",
          decoration: "rgba(59, 130, 246, 0.15)",
        },
        {
          start: "#39D78C",
          end: "#16AD62",
          decoration: "rgba(34, 197, 94, 0.15)",
        },
        {
          start: "#B887FF",
          end: "#8654E8",
          decoration: "rgba(59, 130, 246, 0.15)",
        },
      ],
    };
  },

  computed: {
    ...mapState(useUiStore, {
      loading: "getLoading",
    }),

    safeItems() {
      return Array.isArray(this.items)
        ? this.items.filter(Boolean)
        : [];
    },

    displayItems() {
      return this.showAll
        ? this.safeItems
        : this.safeItems.slice(0, 4);
    },

    skeletonCount() {
      return Math.min(Math.max(this.safeItems.length, 1), 4);
    },

    columnSize() {
      const count = this.safeItems.length;

      if (count >= 4) return 3;
      if (count === 3) return 4;
      if (count === 2) return 6;

      return 12;
    },
  },

  methods: {
    cardVariables(item, index) {
      const palette = this.palettes[index % this.palettes.length];

      return {
        "--icon-start": item.gradientStart || palette.start,
        "--icon-end": item.gradientEnd || palette.end,
        "--card-decoration": item.decorationColor || palette.decoration,
      };
    },

    formatValue(item) {
      const value = item.count ?? 0;

      const formatted =
        typeof value === "number"
          ? value.toLocaleString("en-IN")
          : value;

      return item.currency ? `₹ ${formatted}` : formatted;
    },
  },
};
</script>

<style scoped>
.metric-card {
  isolation: isolate;
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  border-color: rgb(var(--v-theme-primary)) !important;
}

.metric-icon {
  background: linear-gradient(135deg,
      var(--icon-start),
      var(--icon-end));
}

.card-decoration {
  position: absolute;
  right: -35px;
  bottom: -82px;
  width: 175px;
  height: 145px;
  border-radius: 50%;
  background: var(--card-decoration);
  transform: rotate(-25deg);
  z-index: -1;
  pointer-events: none;
}

.skeleton {
  background: linear-gradient(90deg,
      rgb(var(--v-theme-on-surface), 0.07) 25%,
      rgb(var(--v-theme-on-surface), 0.13) 50%,
      rgb(var(--v-theme-on-surface), 0.07) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

.skeleton-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
}

.skeleton-title {
  width: 45%;
  height: 12px;
  border-radius: 6px;
}

.skeleton-value {
  width: 40%;
  height: 26px;
  border-radius: 6px;
}

.skeleton-trend {
  width: 48px;
  height: 18px;
  border-radius: 9px;
}

@keyframes skeleton-shimmer {
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .metric-card {
    transition: none;
  }

  .skeleton {
    animation: none;
  }
}
</style>
